import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { HiArrowLeft, HiArrowRight, HiChevronDown, HiCheckCircle } from 'react-icons/hi';
import servicesData from '../data/servicesData';

const accentMap = {
  pink: {
    gradient: 'from-pink-50 to-rose-50',
    badge: 'bg-pink-100 text-pink-700',
    iconBg: 'bg-pink-100',
    iconText: 'text-pink-600',
    button: 'bg-pink-600 hover:bg-pink-700',
    buttonOutline: 'border-pink-300 text-pink-700 hover:bg-pink-50',
    ring: 'ring-pink-200',
    stepBg: 'bg-pink-600',
    priceBorder: 'border-pink-200',
    pricePopular: 'border-pink-500 bg-pink-50',
    priceTag: 'bg-pink-600',
    dot: 'bg-pink-600',
    lightBg: 'bg-pink-50',
  },
  purple: {
    gradient: 'from-purple-50 to-violet-50',
    badge: 'bg-purple-100 text-purple-700',
    iconBg: 'bg-purple-100',
    iconText: 'text-purple-600',
    button: 'bg-purple-600 hover:bg-purple-700',
    buttonOutline: 'border-purple-300 text-purple-700 hover:bg-purple-50',
    ring: 'ring-purple-200',
    stepBg: 'bg-purple-600',
    priceBorder: 'border-purple-200',
    pricePopular: 'border-purple-500 bg-purple-50',
    priceTag: 'bg-purple-600',
    dot: 'bg-purple-600',
    lightBg: 'bg-purple-50',
  },
  blue: {
    gradient: 'from-blue-50 to-indigo-50',
    badge: 'bg-blue-100 text-blue-700',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-700',
    buttonOutline: 'border-blue-300 text-blue-700 hover:bg-blue-50',
    ring: 'ring-blue-200',
    stepBg: 'bg-blue-600',
    priceBorder: 'border-blue-200',
    pricePopular: 'border-blue-500 bg-blue-50',
    priceTag: 'bg-blue-600',
    dot: 'bg-blue-600',
    lightBg: 'bg-blue-50',
  },
  emerald: {
    gradient: 'from-emerald-50 to-teal-50',
    badge: 'bg-emerald-100 text-emerald-700',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
    button: 'bg-emerald-600 hover:bg-emerald-700',
    buttonOutline: 'border-emerald-300 text-emerald-700 hover:bg-emerald-50',
    ring: 'ring-emerald-200',
    stepBg: 'bg-emerald-600',
    priceBorder: 'border-emerald-200',
    pricePopular: 'border-emerald-500 bg-emerald-50',
    priceTag: 'bg-emerald-600',
    dot: 'bg-emerald-600',
    lightBg: 'bg-emerald-50',
  },
  orange: {
    gradient: 'from-orange-50 to-amber-50',
    badge: 'bg-orange-100 text-orange-700',
    iconBg: 'bg-orange-100',
    iconText: 'text-orange-600',
    button: 'bg-orange-600 hover:bg-orange-700',
    buttonOutline: 'border-orange-300 text-orange-700 hover:bg-orange-50',
    ring: 'ring-orange-200',
    stepBg: 'bg-orange-600',
    priceBorder: 'border-orange-200',
    pricePopular: 'border-orange-500 bg-orange-50',
    priceTag: 'bg-orange-600',
    dot: 'bg-orange-600',
    lightBg: 'bg-orange-50',
  },
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = servicesData.find((s) => s.slug === slug);
  const [openFaq, setOpenFaq] = useState(null);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-display font-bold text-text-dark mb-4">
            Service Not Found
          </h1>
          <p className="text-text-light mb-8">
            The service you're looking for doesn't exist.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-medium hover:bg-primary-dark transition-colors"
          >
            <HiArrowLeft /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const colors = accentMap[service.accentColor];
  const currentIndex = servicesData.findIndex((s) => s.slug === slug);
  const prevService = servicesData[(currentIndex - 1 + servicesData.length) % servicesData.length];
  const nextService = servicesData[(currentIndex + 1) % servicesData.length];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className={`bg-gradient-to-br ${colors.gradient} py-20 px-6 md:px-12 lg:px-24 relative overflow-hidden`}>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="max-w-7xl mx-auto relative z-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-text-light hover:text-text-dark transition-colors mb-8 group"
          >
            <HiArrowLeft className="group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium ${colors.badge} mb-6`}>
                {service.features[0]}
              </span>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-text-dark leading-tight mb-4">
                {service.title}
              </h1>
              <p className="text-xl text-text-light italic mb-4 font-display">
                {service.tagline}
              </p>
              <p className="text-text-light leading-relaxed mb-8 text-lg">
                {service.heroDescription}
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {service.features.map((feature, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-2 bg-white/80 px-4 py-2 rounded-full text-sm font-medium text-text-dark"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
                    {feature}
                  </span>
                ))}
              </div>
              <Link
                to="/"
                onClick={() => setTimeout(() => {
                  const el = document.getElementById('contact');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }, 100)}
                className={`inline-flex items-center gap-2 ${colors.button} text-white px-7 py-3.5 rounded-full font-medium transition-all duration-300 hover:shadow-lg`}
              >
                Get Started <HiArrowRight />
              </Link>
            </div>

            <div className="hidden md:flex justify-center">
              <div className="w-full max-w-md lg:max-w-lg h-80 lg:h-96 rounded-3xl overflow-hidden shadow-xl group/image">
                {service.heroImage ? (
                  <img
                    src={service.heroImage}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover/image:scale-110 group-hover/image:rotate-2"
                  />
                ) : (
                  <div className={`w-full h-full ${colors.iconBg} flex items-center justify-center`}>
                    <service.icon className={`text-8xl lg:text-9xl ${colors.iconText} opacity-60 transition-transform duration-500 group-hover/image:scale-110`} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-primary font-medium text-sm tracking-wider uppercase">
              What's Included
            </span>
            <h2 className="section-title mt-2">
              Service <span className="text-primary">Details</span>
            </h2>
            <p className="section-subtitle">
              Everything you need to know about this service.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {service.details.map((detail, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-8 hover:shadow-lg transition-all duration-500 border border-gray-100 group"
              >
                <div className={`w-14 h-14 ${colors.iconBg} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                  <detail.icon className={`text-2xl ${colors.iconText}`} />
                </div>
                <h3 className="text-xl font-display font-bold text-text-dark mb-3">
                  {detail.title}
                </h3>
                <p className="text-text-light leading-relaxed">
                  {detail.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-primary font-medium text-sm tracking-wider uppercase">
              How It Works
            </span>
            <h2 className="section-title mt-2">
              My <span className="text-primary">Process</span>
            </h2>
            <p className="section-subtitle">
              A simple, transparent process from start to finish.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.process.map((step, index) => (
              <div key={index} className="relative">
                {index < service.process.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] h-[2px] bg-gray-200">
                    <div className={`h-full w-1/2 ${colors.dot}`} />
                  </div>
                )}
                <div className="text-center relative z-10">
                  <div className={`w-16 h-16 ${colors.stepBg} rounded-2xl flex items-center justify-center mx-auto mb-5 text-white font-bold text-lg`}>
                    {step.step}
                  </div>
                  <h3 className="text-lg font-display font-bold text-text-dark mb-2">
                    {step.title}
                  </h3>
                  <p className="text-text-light text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-primary font-medium text-sm tracking-wider uppercase">
              Pricing
            </span>
            <h2 className="section-title mt-2">
              Simple & <span className="text-primary">Transparent</span>
            </h2>
            <p className="section-subtitle">
              Choose the plan that works best for you.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {service.pricing.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-3xl p-8 border-2 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 ${
                  plan.popular
                    ? `${colors.pricePopular} shadow-lg`
                    : 'border-gray-100'
                }`}
              >
                {plan.popular && (
                  <div className={`absolute -top-4 left-1/2 -translate-x-1/2 ${colors.priceTag} text-white text-xs font-bold px-4 py-1.5 rounded-full`}>
                    Most Popular
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 className="text-lg font-display font-bold text-text-dark mb-2">
                    {plan.plan}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-text-dark">
                      {plan.price}
                    </span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <HiCheckCircle className={`${colors.iconText} text-lg flex-shrink-0 mt-0.5`} />
                      <span className="text-text-light text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/"
                  onClick={() => setTimeout(() => {
                    const el = document.getElementById('contact');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }, 100)}
                  className={`block text-center py-3 rounded-xl font-medium transition-all duration-300 ${
                    plan.popular
                      ? `${colors.button} text-white hover:shadow-lg`
                      : `border-2 ${colors.buttonOutline}`
                  }`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-primary font-medium text-sm tracking-wider uppercase">
              FAQ
            </span>
            <h2 className="section-title mt-2">
              Frequently Asked <span className="text-primary">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {service.faq.map((item, index) => (
              <div
                key={index}
                className={`border border-gray-100 rounded-2xl overflow-hidden transition-all duration-300 ${
                  openFaq === index ? `${colors.lightBg} shadow-sm` : 'bg-cream'
                }`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                >
                  <span className="font-medium text-text-dark pr-4">
                    {item.question}
                  </span>
                  <HiChevronDown
                    className={`text-xl text-text-light flex-shrink-0 transition-transform duration-300 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`transition-all duration-300 overflow-hidden ${
                    openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="px-6 pb-5 text-text-light leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`bg-gradient-to-br ${colors.gradient} py-20 px-6 md:px-12 lg:px-24`}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-text-dark mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-text-light text-lg mb-8">
            Let's work together to achieve your goals. Get in touch today for a
            free consultation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/"
              onClick={() => setTimeout(() => {
                const el = document.getElementById('contact');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }, 100)}
              className={`inline-flex items-center gap-2 ${colors.button} text-white px-8 py-3.5 rounded-full font-medium transition-all duration-300 hover:shadow-lg`}
            >
              Contact Me <HiArrowRight />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-white text-text-dark px-8 py-3.5 rounded-full font-medium hover:shadow-lg transition-all duration-300"
            >
              <HiArrowLeft /> Back to Home
            </Link>
          </div>
        </div>
      </section>

      {/* Navigation Between Services */}
      <section className="py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link
              to={`/services/${prevService.slug}`}
              className="group flex items-center gap-3 text-text-light hover:text-primary transition-colors"
            >
              <HiArrowLeft className="group-hover:-translate-x-1 transition-transform" />
              <div>
                <p className="text-xs uppercase tracking-wider">Previous</p>
                <p className="font-display font-bold text-text-dark group-hover:text-primary transition-colors">
                  {prevService.title}
                </p>
              </div>
            </Link>
            <Link
              to={`/services/${nextService.slug}`}
              className="group flex items-center gap-3 text-text-light hover:text-primary transition-colors text-right"
            >
              <div>
                <p className="text-xs uppercase tracking-wider">Next</p>
                <p className="font-display font-bold text-text-dark group-hover:text-primary transition-colors">
                  {nextService.title}
                </p>
              </div>
              <HiArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
